import React, { Component } from 'react';
import '../../../App.scss';
import 'antd/dist/antd.css';
import {
  Button, Card, Divider, Input, PageHeader, Popconfirm, Space, Table, Typography, message,
} from 'antd';
import { DeleteOutlined, PlusCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import firebase from 'firebase/app';

import { getTemplatePartner } from '../../../models/useTemplatePartners';
import { unbindMemberId, unbindErrorMessages, tagIdForBinding } from '../../../models/unbindMemberId';
import { ConnectedPartnerBadge, tagUidFromTagId } from '../../../components/client/ConnectedPartnerBadge';

const { Paragraph, Text } = Typography;

/**
 * Strike Admin repair screen for team member integration-ID bindings.
 *
 * Lists teams/{teamId}/memberIds docs (doc id = {templateTagId}---{xmid}) and
 * supports DELETE of a binding and CREATE of a new one, keeping the member
 * doc's externalIds mirror (teams/{teamId}/members/{memberClientId}) in sync.
 *
 * Writes go through the webapp Firestore SDK as admin (rules permit admins),
 * following the AdminTemplateTagScreen direct-write pattern.
 */
export default class AdminMemberIdsScreen extends Component {
  constructor(props) {
    super(props);

    this.state = {
      teamIdInput: '',
      teamId: null,
      teamName: null,
      bindings: [],
      partnersByTemplate: {},
      loading: false,
      // create form
      newTemplateTagId: '',
      newXmid: '',
      newMemberClientId: '',
      newTagId: '',
      newWebhookConfigId: '',
      creating: false,
    };
  }

  loadBindings = async (teamId) => {
    if (!teamId) return;
    this.setState({ loading: true, teamId });
    try {
      const db = firebase.firestore();
      const [teamDoc, snapshot] = await Promise.all([
        db.collection('teams').doc(teamId).get(),
        db.collection('teams').doc(teamId).collection('memberIds').get(),
      ]);
      const bindings = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      this.setState({
        bindings,
        teamName: teamDoc.exists ? (teamDoc.data().teamName || null) : null,
        loading: false,
      });
      if (!teamDoc.exists) {
        message.warning(`No team doc found for teamId ${teamId}`);
      }
      this.loadPartners(bindings);
    } catch (err) {
      console.error('loadBindings failed:', err);
      message.error(`Could not load bindings: ${err.message}`);
      this.setState({ loading: false, bindings: [] });
    }
  }

  // Resolve each templateTagId -> partner branding (avatar + name), cached per template.
  loadPartners = async (bindings) => {
    const templateIds = [...new Set(bindings.map((b) => b.templateTagId).filter(Boolean))];
    const entries = await Promise.all(
      templateIds.map(async (id) => [id, await getTemplatePartner(id)]),
    );
    const partnersByTemplate = {};
    entries.forEach(([id, partner]) => {
      if (partner) partnersByTemplate[id] = partner;
    });
    this.setState({ partnersByTemplate });
  }

  onClickLoad = () => {
    const teamId = this.state.teamIdInput.trim();
    if (!teamId) {
      message.warning('Enter a teamId first');
      return;
    }
    this.loadBindings(teamId);
  }

  // Keep the member doc's externalIds mirror consistent with the binding docs.
  updateMemberMirror = async (memberClientId, templateTagId, value) => {
    const { teamId } = this.state;
    if (!memberClientId || !templateTagId) return;
    const memberRef = firebase.firestore()
      .collection('teams').doc(teamId)
      .collection('members').doc(memberClientId);
    try {
      await memberRef.update({
        [`externalIds.${templateTagId}`]: value,
        updatedAt: firebase.firestore.Timestamp.now(),
      });
    } catch (err) {
      // Surface but don't fail the whole operation — the binding write already landed.
      console.error('externalIds mirror update failed:', err);
      message.warning(`Binding written, but member doc mirror update failed: ${err.message}`);
    }
  }

  /**
   * Release through the strike-payer API, not by deleting the document here.
   *
   * This screen used to delete the binding and clear the mirror itself, which was
   * safe while onDeleteMemberId existed to emit member_removed behind it. That
   * trigger is gone — Gen1 Firestore triggers cannot be created against this
   * database — so a direct delete now tells the partner nothing, and a partner
   * who is never told an ID is free will never reissue it. The ID is stranded.
   *
   * The API does all three in one place: deletes the binding and clears the
   * mirror in a single batch, then emits. It accepts a Strike admin, which is who
   * is looking at this screen.
   */
  onDeleteBinding = async (binding) => {
    const { teamId, bindings } = this.state;
    // The API is addressed by tag, as join is. A hand-repaired binding may lack
    // its own tagId; a same-template sibling resolves identically, and this is
    // the same fallback the create form uses to fill tagId in.
    const tagId = tagIdForBinding(binding, bindings);
    if (!tagId) {
      message.error(
        `Binding ${binding.id} has no tagId and no same-template sibling to borrow one from — `
        + 'set a tagId on the binding doc first.',
      );
      return;
    }
    const result = await unbindMemberId({ tagId, xmid: binding.xmid });

    if (!result.ok) {
      console.error('Delete binding failed:', result.error);
      message.error(unbindErrorMessages[result.error] || `Delete failed: ${result.error}`);
      return;
    }
    message.success(`Deleted binding ${binding.id}`);
    this.loadBindings(teamId);
  }

  onCreateBinding = async () => {
    const { teamId, bindings } = this.state;
    const templateTagId = this.state.newTemplateTagId.trim();
    const xmid = this.state.newXmid.trim();
    const memberClientId = this.state.newMemberClientId.trim();
    const tagId = this.state.newTagId.trim();
    let webhookConfigId = this.state.newWebhookConfigId.trim();

    if (!teamId) { message.warning('Load a team first'); return; }
    if (!templateTagId || !xmid || !memberClientId) {
      message.warning('templateTagId, xmid and memberClientId are all required');
      return;
    }

    // webhookConfigId: copy from an existing binding for the same template when
    // not entered manually (all bindings for one template share a config).
    if (!webhookConfigId) {
      const source = bindings.find(
        (b) => b.templateTagId === templateTagId && b.webhookConfigId,
      ) || bindings.find((b) => b.webhookConfigId);
      if (source) webhookConfigId = source.webhookConfigId;
    }
    if (!webhookConfigId) {
      message.warning('Enter a webhookConfigId — no existing binding to copy it from');
      return;
    }

    const docId = `${templateTagId}---${xmid}`;
    this.setState({ creating: true });
    try {
      const db = firebase.firestore();
      const bindingRef = db.collection('teams').doc(teamId).collection('memberIds').doc(docId);
      const existing = await bindingRef.get();
      if (existing.exists) {
        message.error(`Binding ${docId} already exists — delete it first`);
        this.setState({ creating: false });
        return;
      }
      const doc = {
        teamId,
        templateTagId,
        xmid,
        memberClientId,
        // Same-template sibling fallback — the sibling resolves to the same
        // (team, template), and a binding without a tagId cannot be released
        // through the tag-addressed unbind API later.
        tagId: tagId || tagIdForBinding({ templateTagId }, bindings),
        webhookConfigId,
        boundVia: 'admin',
        boundAt: firebase.firestore.Timestamp.now(),
      };
      await bindingRef.set(doc);
      await this.updateMemberMirror(memberClientId, templateTagId, xmid);
      message.success(`Created binding ${docId}`);
      this.setState({
        creating: false,
        newTemplateTagId: '',
        newXmid: '',
        newMemberClientId: '',
        newTagId: '',
        newWebhookConfigId: '',
      });
      this.loadBindings(teamId);
    } catch (err) {
      console.error('Create binding failed:', err);
      message.error(`Create failed: ${err.message}`);
      this.setState({ creating: false });
    }
  }

  bindingColumns = () => ([
    { title: 'Binding (doc id)', dataIndex: 'id', key: 'id', render: (v) => <Text code>{v}</Text> },
    {
      title: 'Template',
      dataIndex: 'templateTagId',
      key: 'templateTagId',
      render: (v) => (
        <Space size={4}>
          <ConnectedPartnerBadge partner={this.state.partnersByTemplate[v]} />
          {v}
        </Space>
      ),
    },
    {
      title: 'tagUid',
      key: 'tagUid',
      render: (unused, binding) => <Text code>{tagUidFromTagId(binding.tagId) || ''}</Text>,
    },
    { title: 'xmid', dataIndex: 'xmid', key: 'xmid' },
    { title: 'Member clientId', dataIndex: 'memberClientId', key: 'memberClientId', render: (v) => <Text code>{v}</Text> },
    { title: 'Bound via', dataIndex: 'boundVia', key: 'boundVia' },
    {
      title: 'Bound at',
      dataIndex: 'boundAt',
      key: 'boundAt',
      render: (v) => (v && v.toDate ? v.toDate().toISOString() : `${v || ''}`),
    },
    { title: 'tagId', dataIndex: 'tagId', key: 'tagId', render: (v) => <Text code>{v}</Text> },
    { title: 'webhookConfigId', dataIndex: 'webhookConfigId', key: 'webhookConfigId', render: (v) => <Text code>{v}</Text> },
    {
      title: '',
      key: 'actions',
      render: (unused, binding) => (
        <Popconfirm
          title={`Delete binding ${binding.id}? This also clears the member's externalIds mirror.`}
          onConfirm={() => this.onDeleteBinding(binding)}
          okText="Delete"
          okButtonProps={{ danger: true }}
        >
          <Button danger type="text" icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ]);

  render() {
    const {
      teamIdInput, teamId, teamName, bindings, loading,
      newTemplateTagId, newXmid, newMemberClientId, newTagId, newWebhookConfigId, creating,
    } = this.state;

    return (
      <div style={{ height: '100%' }}>
        <PageHeader
          ghost={false}
          title="Member Integration IDs"
          subTitle="Repair xmid bindings (teams/{teamId}/memberIds)"
          onBack={(() => window.history.back())}
        />

        <div style={{ padding: '16px', maxWidth: '1200px' }}>
          <Input.Group compact style={{ display: 'flex', width: '100%', maxWidth: 520 }}>
            <Input
              placeholder="teamId"
              value={teamIdInput}
              onChange={(e) => this.setState({ teamIdInput: e.target.value })}
              onPressEnter={this.onClickLoad}
              style={{ flex: 1 }}
            />
            <Button type="primary" icon={<ReloadOutlined />} loading={loading} onClick={this.onClickLoad}>
              Load
            </Button>
          </Input.Group>

          {teamId && (
            <>
              <Divider orientation="left">
                Bindings for {teamName ? `${teamName} (${teamId})` : teamId}
              </Divider>
              <Table
                rowKey="id"
                size="small"
                loading={loading}
                columns={this.bindingColumns()}
                dataSource={bindings}
                pagination={false}
                locale={{ emptyText: 'No memberIds bindings for this team' }}
                scroll={{ x: true }}
              />

              <Divider orientation="left">Create binding</Divider>
              <Card style={{ maxWidth: 520 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Input
                    placeholder="templateTagId (e.g. orbxi)"
                    value={newTemplateTagId}
                    onChange={(e) => this.setState({ newTemplateTagId: e.target.value })}
                  />
                  <Input
                    placeholder="xmid (partner's member ID, e.g. 439)"
                    value={newXmid}
                    onChange={(e) => this.setState({ newXmid: e.target.value })}
                  />
                  <Input
                    placeholder="memberClientId (member doc id in this team)"
                    value={newMemberClientId}
                    onChange={(e) => this.setState({ newMemberClientId: e.target.value })}
                  />
                  <Input
                    placeholder="tagId (optional — copied from an existing binding if blank)"
                    value={newTagId}
                    onChange={(e) => this.setState({ newTagId: e.target.value })}
                  />
                  <Input
                    placeholder="webhookConfigId (optional — copied from an existing binding if blank)"
                    value={newWebhookConfigId}
                    onChange={(e) => this.setState({ newWebhookConfigId: e.target.value })}
                  />
                  <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                    Writes teams/{'{teamId}'}/memberIds/{'{templateTagId}'}---{'{xmid}'} with
                    boundVia &quot;admin&quot;, and mirrors the ID onto the member doc&apos;s
                    externalIds map.
                  </Paragraph>
                  <Button
                    type="primary"
                    icon={<PlusCircleOutlined />}
                    loading={creating}
                    onClick={this.onCreateBinding}
                  >
                    Create binding
                  </Button>
                </Space>
              </Card>
            </>
          )}
        </div>
      </div>
    );
  }
}
